import React, { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductUpdatesEntry } from './types';
import { mockProductUpdatesEntries, MOCK_ENDPOINT } from './mockData';

const PRODUCT_UPDATES_QUERY = `
  query GetProductUpdatesEntries($limit: Int!) {
    entries(limit: $limit) {
      ... on productUpdate_Entry {
        id
        title
        uri
        postDate
        productUpdateTags {
          id
          title
        }
        productUpdateCategory {
          id
          title
        }
        richText
      }
    }
  }
`;

export interface UseProductUpdatesDataProps {
  endpoint: string;
  headers?: Record<string, string>;
  limit?: number;
  enabled?: boolean;
  mockMode?: {
    state: 'loading' | 'error' | 'empty' | 'success';
    data?: ProductUpdatesEntry[];
    error?: string;
  };
}

export interface UseProductUpdatesDataResult {
  data: ProductUpdatesEntry[] | null;
  loading: boolean;
  error: string | null;
  getData: () => Promise<void>;
}

const fetchProductUpdates = async (
  endpoint: string,
  limit: number,
  headers: Record<string, string> = {},
): Promise<ProductUpdatesEntry[]> => {
  // Skip actual request for mock endpoints
  if (endpoint === MOCK_ENDPOINT) {
    return mockProductUpdatesEntries.slice(0, limit);
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({
      query: PRODUCT_UPDATES_QUERY,
      variables: { limit },
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const responseData = await response.json();

  if (responseData.errors) {
    throw new Error(responseData.errors[0]?.message || 'GraphQL error');
  }

  return responseData.data.entries;
};

export const useProductUpdatesData = ({
  endpoint,
  headers,
  limit = 10,
  enabled = true,
  mockMode,
}: UseProductUpdatesDataProps): UseProductUpdatesDataResult => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['productUpdates', endpoint, headers, limit],
    [endpoint, headers, limit],
  );

  const query = useQuery({
    queryKey,
    queryFn: () => fetchProductUpdates(endpoint, limit, headers),
    enabled: !mockMode && enabled,
    retry: false,
  });

  // Clear the query cache when the modal closes
  React.useEffect(() => {
    if (!enabled && !mockMode) {
      queryClient.removeQueries({ queryKey });
    }
  }, [enabled, mockMode, queryClient, queryKey]);

  // Handle mock mode
  if (mockMode) {
    return {
      data: mockMode.data?.slice(0, limit) || null,
      loading: mockMode.state === 'loading',
      error: mockMode.error || null,
      getData: async () => {
        // Mock implementation - no-op
      },
    };
  }

  return {
    data: query.data || null,
    loading: query.isLoading,
    error: query.error ? (query.error as Error).message : null,
    getData: async () => {
      await query.refetch();
    },
  };
};
